import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { ForgotPasswordDialog } from "@/components/auth/forgot-password-dialog";
import { TwoFactorChallenge } from "@/components/auth/two-factor-challenge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { mfaIdFrom } from "@/lib/api/two-factor";
import { useAuth } from "@/lib/session/auth-context";
import { applyServerErrors } from "@/lib/utils/form-errors";

export function LoginPage() {
  const { t } = useTranslation();
  const { isAuthenticated, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The website's sign-up buttons link to /login?tab=register.
  const [params] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  // Set when the password was right and the account wants its second factor (SEC-1).
  const [mfaId, setMfaId] = useState<string | null>(null);
  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/dashboard";
  // An invitation link sends its address along (routes/invite.tsx), so nobody retypes it.
  const invitedEmail = params.get("email") ?? "";

  // Schemas live inside the component so validation messages follow the current language.
  const loginSchema = z.object({
    email: z.email(t("validation.email")),
    password: z.string().min(8, t("validation.password")),
  });
  const registerSchema = loginSchema.extend({
    name: z.string().min(2, t("validation.name")),
    // The AGB, with the AVV, are accepted at sign-up (LEG-1); the version goes onto the user.
    terms: z.boolean().refine(Boolean, t("login.termsRequired")),
  });
  type LoginValues = z.infer<typeof loginSchema>;
  type RegisterValues = z.infer<typeof registerSchema>;

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: invitedEmail, password: "" },
  });
  const registerForm = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: "", email: invitedEmail, password: "", terms: false },
  });

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;
  if (mfaId) {
    return (
      <div className="mx-auto max-w-md">
        <TwoFactorChallenge mfaId={mfaId} onCancel={() => setMfaId(null)} />
      </div>
    );
  }

  async function run(
    action: () => Promise<void>,
    successMessage: string,
    setError: Parameters<typeof applyServerErrors>[1],
  ) {
    setSubmitting(true);
    try {
      await action();
      toast.success(successMessage);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const pending = mfaIdFrom(error);
      if (pending) return setMfaId(pending);
      const message = applyServerErrors(error, setError);
      if (message) toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-md">
      {redirectTo.startsWith("/invite/") && (
        <p className="mb-4 text-sm text-muted-foreground" data-testid="login-invite-hint">
          {t("login.inviteHint")}
        </p>
      )}
      <Tabs defaultValue={params.get("tab") === "register" ? "register" : "login"}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="login">{t("login.tabLogin")}</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">
            {t("login.tabRegister")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <Card>
            <CardHeader>
              <CardTitle>{t("login.welcome")}</CardTitle>
              <CardDescription>{t("login.welcomeSubtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Form {...loginForm}>
                <form
                  data-testid="login-form"
                  className="space-y-4"
                  onSubmit={loginForm.handleSubmit((values) =>
                    run(
                      () => login(values.email, values.password),
                      t("login.signedIn"),
                      (name, err) => loginForm.setError(name as "email" | "password", err),
                    ),
                  )}
                >
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.email")}</FormLabel>
                        <FormControl>
                          <Input type="email" autoComplete="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.password")}</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="current-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {t("login.submit")}
                  </Button>
                  <div className="text-center">
                    <ForgotPasswordDialog />
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="register">
          <Card>
            <CardHeader>
              <CardTitle>{t("login.createTitle")}</CardTitle>
              <CardDescription>{t("login.createSubtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Form {...registerForm}>
                <form
                  data-testid="register-form"
                  className="space-y-4"
                  onSubmit={registerForm.handleSubmit((values) =>
                    run(
                      () => register(values),
                      t("login.created"),
                      (name, err) =>
                        registerForm.setError(name as "name" | "email" | "password", err),
                    ),
                  )}
                >
                  <FormField
                    control={registerForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.name")}</FormLabel>
                        <FormControl>
                          <Input autoComplete="name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.email")}</FormLabel>
                        <FormControl>
                          <Input type="email" autoComplete="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.password")}</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="terms"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-start gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked === true)}
                              className="mt-0.5"
                              data-testid="register-terms"
                            />
                          </FormControl>
                          <FormLabel className="text-sm leading-snug font-normal">
                            <Trans
                              i18nKey="login.terms"
                              components={{
                                terms: <LegalLink to="/agb" />,
                                privacy: <LegalLink to="/datenschutz" />,
                                dpa: <LegalLink to="/avv" />,
                              }}
                            />
                          </FormLabel>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {t("login.create")}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** A legal page from the sign-up checkbox, in a new tab so the form keeps what was typed. */
function LegalLink({ to, children }: { to: string; children?: React.ReactNode }) {
  return (
    <Link to={to} target="_blank" rel="noreferrer" className="underline underline-offset-2">
      {children}
    </Link>
  );
}
